import { useEffect, useRef, useState } from 'react';
import { coverageSummary } from '@/data/cities';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Search,
  ArrowRight,
  Check,
  Circle,
  Bandage,
  Sprout,
  Bot,
  Hospital,
  ListChecks,
  MapPin,
  Star,
  BadgeCheck,
  CalendarCheck,
  TrendingUp,
  Calendar,
  Lock,
  Image as ImageIcon,
} from 'lucide-react';
import { Seo, OG_IMAGE_HOME } from '@/seo/Seo';
import { faqSchema, breadcrumbSchema } from '@/seo/schemas';
import { configApi } from '@/api/configApi';
import { centresApi } from '@/api/centresApi';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { useAiChat } from '@/components/ai/useAiChat';
import { ReviewText } from '@/components/reviews/ReviewText';
import { pathUrl } from '@/utils/catalogPaths';

/* ------------------------------------------------------------------ */
/*  Reusable fade-in-on-scroll wrapper                                */
/* ------------------------------------------------------------------ */
function FadeIn({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Rotating hero heading - fixed height, zero layout shift.          */
/*  Every phrase shares one CSS-grid cell, so the box is always as    */
/*  tall as the longest phrase. Only the active phrase is visible.    */
/* ------------------------------------------------------------------ */
function RotatingHeading({ phrases }: { phrases: string[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (phrases.length <= 1) return;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % phrases.length);
    }, 3800);
    return () => clearInterval(id);
  }, [phrases.length]);

  return (
    <h1 className="text-[38px] font-extrabold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-[48px] lg:text-[54px]">
      <span className="block text-foreground/90">Experience</span>
      {/* Fixed-height stage: an invisible sizer reserves the tallest phrase's
          height so the layout never shifts; the animated phrase sits on top. */}
      <span className="relative block">
        <span aria-hidden className="invisible grid">
          {phrases.map((phrase) => (
            <span key={phrase} className="col-start-1 row-start-1">
              {phrase}
            </span>
          ))}
        </span>
        <span className="absolute inset-0">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={index}
              className="block text-primary will-change-transform"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
                opacity: { duration: 0.32, ease: 'easeOut' },
              }}
            >
              {phrases[index]}
            </motion.span>
          </AnimatePresence>
        </span>
      </span>
    </h1>
  );
}

/* ================================================================== */
/*  HOME PAGE                                                         */
/* ================================================================== */
export default function Home() {
  const navigate = useNavigate();
  const config = useSiteConfig();

  const stats = config.stats;
  // Start from the figures the prerender baked in, so the first paint shows
  // the real count rather than a guess that then jumps.
  const [liveLocations, setLiveLocations] = useState<number | null>(
    () => (typeof window !== 'undefined' ? window.__HOME_STATS__?.locations : null) ?? null,
  );
  const [liveRating, setLiveRating] = useState<number | null>(
    () => (typeof window !== 'undefined' ? window.__HOME_STATS__?.rating : null) ?? null,
  );
  const [liveFailed, setLiveFailed] = useState(false);
  const [topReviews, setTopReviews] = useState<
    { user_name: string; service_name: string; rating: number; review: string; city: string }[]
  >([]);
  const [query, setQuery] = useState('');

  const heroPhrases = [
    config.hero.heading,
    config.hero.heading2,
    config.hero.heading3,
    config.hero.heading4,
    config.hero.heading5,
    config.hero.heading6,
  ].filter(Boolean) as string[];

  /* Live data from the Recovry backend */
  useEffect(() => {
    centresApi
      .list({ limit: 50 })
      .then((res) => {
        const centreList: { services?: { reviews?: { rating?: number }[] }[] }[] =
          res.data?.centres ?? [];
        // The list is one page; the total is the figure patients should see.
        const locations = typeof res.data?.total === 'number' ? res.data.total : centreList.length;
        setLiveLocations(locations);
        const allRatings = centreList.flatMap((c) =>
          (c.services ?? []).flatMap((s) =>
            (s.reviews ?? [])
              .map((r) => r.rating)
              .filter((r): r is number => typeof r === 'number'),
          ),
        );
        let rating: number | null = null;
        if (allRatings.length > 0) {
          const avg = allRatings.reduce((a, b) => a + b, 0) / allRatings.length;
          rating = Math.round(avg * 10) / 10;
          setLiveRating(rating);
        }
        if (typeof window !== 'undefined' && window.__PRERENDER__) {
          window.__HOME_STATS__ = { locations, rating };
        }
      })
      // No live figures: fall back to the admin's numbers rather than a blank tile.
      .catch(() => setLiveFailed(true));

    // Real patient stories from mongo (top-rated reviews with written text).
    configApi
      .getTopReviews()
      .then((res) => {
        const list = res.data?.reviews;
        if (Array.isArray(list) && list.length > 0) setTopReviews(list);
      })
      .catch(() => {});
  }, []);

  const goSearch = (term: string) => {
    const t = term.trim();
    // Services-first: show matching services, not a wall of clinics (PDF search §1).
    navigate(t ? `/search?q=${encodeURIComponent(t)}` : '/centres');
  };

  const { openChat } = useAiChat();
  const handleAskAi = () => {
    openChat({ seed: '', context: { source: 'home_card' } });
  };

  const searchHints = ['Back pain', 'Knee injury', 'Post-surgery', 'Neck pain', 'Sciatica', 'Shoulder pain'];

  // A figure is shown only once it is real: the live count, or the admin's
  // number when the live fetch has failed. Until then the tile keeps its
  // shape and shows a placeholder, so nothing jumps from "13+" to "43+".
  // The admin's figures stand in only when the live fetch failed AND they are
  // real numbers: a stored 0 or a stale count is not worth showing.
  const clinicsCount = liveLocations ?? (liveFailed && stats.locations_count > 0 ? stats.locations_count : null);
  const ratingValue = liveRating ?? (liveFailed && stats.average_rating > 0 ? stats.average_rating : null);
  const servicesCount = config.loaded ? stats.services_count : null;

  // A tile with nothing real to show after the fetch has settled is dropped,
  // not left as a permanent placeholder.
  const statItems = [
    { icon: Hospital, num: clinicsCount == null ? null : `${clinicsCount}+`, label: 'Verified clinics' },
    { icon: ListChecks, num: servicesCount == null ? null : `${servicesCount}+`, label: 'Physiotherapy services' },
    { icon: MapPin, num: 'PAN India' },
    { icon: Star, num: ratingValue == null ? null : `${ratingValue}`, label: 'Average clinic rating' },
    { icon: BadgeCheck, num: 'DPIIT', label: 'Recognised startup' },
  ];

  const steps = [
    {
      icon: Search,
      title: 'Describe your problem',
      desc: 'Search by symptom, body part, or browse our structured service categories. The AI assistant will recommend the right therapy if you are unsure.',
    },
    {
      icon: MapPin,
      title: 'Find a clinic near you',
      desc: 'Filter by location, price, rating, home visit, and language. Compare verified clinics side by side and read real patient reviews.',
    },
    {
      icon: CalendarCheck,
      title: 'Book & attend your session',
      desc: 'Pick your date, time, and preferred physiotherapist. Confirm in seconds, no calls, no waiting on hold. Show up and recover.',
    },
  ];

  const avatarColors = ['var(--sc-teal-mid)', 'var(--sc-teal)', 'var(--sc-amber)', 'var(--sc-blue)'];

  // Patient stories, all from mongo: real top-rated reviews first, otherwise the
  // curated stories stored on the site config. Nothing is hardcoded here.
  const storySource =
    topReviews.length > 0
      ? topReviews.slice(0, 3).map((r) => ({
          text: r.review,
          name: r.user_name,
          detail: [r.service_name, r.city].filter(Boolean).join(' · '),
          rating: r.rating,
        }))
      : config.testimonials.slice(0, 3);

  const testimonials = storySource.map((t, i) => ({
    text: t.text,
    name: t.name,
    detail: t.detail,
    rating: Math.max(1, Math.min(5, Math.round(t.rating || 5))),
    avatar: (t.name || 'P').charAt(0).toUpperCase(),
    color: avatarColors[i % avatarColors.length],
  }));

  const clinicBenefits = [
    { icon: TrendingUp, label: 'More patient bookings' },
    { icon: Calendar, label: 'Online scheduling' },
    { icon: Star, label: 'Build your rating' },
    { icon: Lock, label: 'Transparent payouts' },
  ];

  const { static: staticBanner, promo: promoBanner } = config.banners;
  // Derive the display ratio from the image's pixel dimensions (set in mongo)
  // so the banner shows uncropped. Falls back to 5:2 if dimensions are missing.
  const bannerRatio = (b: { width?: number; height?: number }) =>
    b.width && b.height ? `${b.width} / ${b.height}` : '5 / 2';

  /* ================================================================ */
  return (
    <div className="overflow-x-clip bg-background">
      <Seo
        title="Recovry | Verified Physiotherapy Clinics Near You"
        description="Find Recovry-verified physiotherapy and rehab clinics across PAN India. Search by symptom or service, compare clinics, read reviews, and book in minutes."
        canonical="/"
        ogTitle="Recovry – Physio & Pain Relief"
        ogDescription="Verified physiotherapists. Book in minutes."
        ogImage={OG_IMAGE_HOME}
        keywords="physiotherapy near me, physiotherapy clinic, back pain therapy, knee pain therapy, sports injury rehabilitation, post surgical rehab, neuro rehab, sciatica treatment, find physiotherapy clinic near me, Recovry"
        schema={[
          breadcrumbSchema([{ name: 'Home', path: '/' }]),
          faqSchema([
            {
              question: 'How do I find a verified physiotherapy clinic near me on Recovry?',
              answer:
                'Search by symptom or service on the Find Recovry page, sort by your location, and filter by speciality, price, rating, home visit, and language. Every clinic is verified by Recovry before going live.',
            },
            {
              question: 'How do I book a physiotherapy session through Recovry?',
              answer:
                'Pick a clinic, choose the service, select a date and time slot, and confirm. You can pay online or at the clinic depending on the clinic policy.',
            },
            {
              question: 'Does Recovry offer home physiotherapy?',
              answer:
                'Some clinics on Recovry offer at-home physiotherapy in addition to in-clinic visits. Look for the home-visit tag on the clinic detail page.',
            },
            {
              question: 'Which cities does Recovry cover?',
              answer:
                `Recovry has verified partner clinics in ${coverageSummary()}, with more cities added regularly.`,
            },
          ]),
        ]}
      />

      {/* ============================================================ */}
      {/*  HERO                                                        */}
      {/* ============================================================ */}
      <section
        className="relative overflow-hidden border-b"
        style={{ background: 'linear-gradient(160deg, #FFFFFF 0%, #F2F8F5 58%, #E5F2EC 100%)' }}
        aria-label="Find verified physiotherapy clinics near you in PAN India"
      >
        {/* Ambient atmosphere - soft brand glows + faint dot grid */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div
            className="absolute -right-32 -top-32 h-[34rem] w-[34rem] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(29,158,117,0.18), transparent 68%)' }}
          />
          <div
            className="absolute -left-40 top-1/3 h-[30rem] w-[30rem] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(15,110,86,0.10), transparent 70%)' }}
          />
        </div>

        <div className="relative mx-auto grid max-w-[1440px] items-start gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20 lg:px-12 lg:py-20">
          {/* Left: copy + search */}
          <motion.div
            className="min-w-0"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-secondary px-3 py-1.5 text-xs font-semibold text-primary">
              <Circle className="size-2 fill-current" aria-hidden="true" />
              {config.hero.welcome_text}
            </span>

            <RotatingHeading phrases={heroPhrases} />

            <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-muted-foreground">
              {config.hero.subheading}
            </p>

            {/* Search bar */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                goSearch(query);
              }}
              className="mt-7 flex max-w-lg items-center gap-2 rounded-xl border border-[var(--sc-border-strong)] bg-card p-1.5 pl-4 shadow-sm focus-within:border-[var(--sc-teal-mid)]"
            >
              <Search className="size-5 shrink-0 text-[var(--sc-hint)]" aria-hidden="true" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by symptom, service, or area..."
                aria-label="Search physiotherapy clinics and services"
                className="w-full min-w-0 flex-1 bg-transparent text-[15px] text-foreground outline-none placeholder:text-[var(--sc-hint)]"
              />
              <button
                type="submit"
                className="shrink-0 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[var(--sc-teal-dark)]"
              >
                Search
              </button>
            </form>

            {/* Hint chips */}
            <div className="mt-4 flex flex-wrap gap-2">
              {searchHints.map((hint) => (
                <button
                  key={hint}
                  onClick={() => goSearch(hint)}
                  className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/30 hover:bg-secondary hover:text-primary"
                >
                  {hint}
                </button>
              ))}
            </div>

            {/* Trust row */}
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Check className="size-4 text-[var(--sc-teal-mid)]" /> Verified clinics only
              </span>
              <span className="hidden h-3.5 w-px bg-[var(--sc-border-strong)] sm:inline-block" />
              <span className="flex items-center gap-1.5">
                <Check className="size-4 text-[var(--sc-teal-mid)]" /> Free to browse
              </span>
              <span className="hidden h-3.5 w-px bg-[var(--sc-border-strong)] sm:inline-block" />
              <span className="flex items-center gap-1.5">
                <Check className="size-4 text-[var(--sc-teal-mid)]" /> Hindi &amp; English support
              </span>
            </div>
          </motion.div>

          {/* Right: entry cards + AI card */}
          <motion.div
            className="flex min-w-0 flex-col gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Entry card A - pain (featured) */}
            <button
              onClick={() => navigate(pathUrl('A'))}
              className="group rounded-2xl border-[1.5px] border-[var(--sc-teal-mid)] bg-gradient-to-br from-secondary to-card p-6 text-left transition-shadow hover:shadow-[0_6px_20px_rgba(15,110,86,0.1)]"
            >
              <div className="mb-2.5 flex items-start justify-between">
                <span className="flex size-10 items-center justify-center rounded-xl bg-secondary text-primary">
                  <Bandage className="size-5" />
                </span>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold text-primary-foreground">
                    Most searched
                  </span>
                  <ArrowRight className="size-5 text-primary transition-transform group-hover:translate-x-1" />
                </div>
              </div>
              <h2 className="text-lg font-bold text-foreground">I have pain or an injury</h2>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Back, knee, neck, sports injuries, post-surgery, neuro conditions. Find the right treatment, fast.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {['Back pain', 'Knee injury', 'Sciatica', '+8 more'].map((t) => (
                  <span key={t} className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                    {t}
                  </span>
                ))}
              </div>
            </button>

            {/* Entry card B - wellness */}
            <button
              onClick={() => navigate(pathUrl('B'))}
              className="group rounded-2xl border border-border bg-card p-6 text-left transition-all hover:border-[var(--sc-teal-mid)] hover:shadow-[0_6px_20px_rgba(15,110,86,0.08)]"
            >
              <div className="mb-2.5 flex items-start justify-between">
                <span
                  className="flex size-10 items-center justify-center rounded-xl"
                  style={{ background: 'var(--sc-amber-light)', color: 'var(--sc-amber)' }}
                >
                  <Sprout className="size-5" />
                </span>
                <ArrowRight className="size-5 text-primary transition-transform group-hover:translate-x-1" />
              </div>
              <h2 className="text-lg font-bold text-foreground">I want to improve my health</h2>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Posture correction, fitness recovery, senior wellness, women's health. Move better and stay well.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {['Posture', 'Active recovery', '+4 more'].map((t) => (
                  <span key={t} className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                    {t}
                  </span>
                ))}
              </div>
            </button>

            {/* AI card - opens the Recovry AI chat */}
            <button
              type="button"
              onClick={handleAskAi}
              className="flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors hover:brightness-[0.98]"
              style={{ background: 'var(--sc-blue-light)', borderColor: '#B5D4F4' }}
            >
              <span
                className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-card"
                style={{ color: 'var(--sc-blue)' }}
              >
                <Bot className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-foreground">Not sure what you need?</div>
                <div className="text-xs text-muted-foreground">
                  Describe your problem and our AI recommends the right service
                </div>
              </div>
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold"
                style={{ background: 'var(--sc-blue)', color: '#fff' }}
              >
                Ask AI <ArrowRight className="size-3" />
              </span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  STATS STRIP                                                 */}
      {/* ============================================================ */}
      <div className="border-b bg-card">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-x-6 gap-y-5 px-5 py-7 sm:px-8 lg:px-12">
          {statItems.filter((s) => s.num !== null || !liveFailed).map((s, i) => (
            <div key={s.label} className="flex items-center gap-3">
              <span className="flex size-9 items-center justify-center rounded-lg bg-secondary text-primary">
                <s.icon className="size-[18px]" />
              </span>
              <div>
                <div className="text-xl font-extrabold text-foreground">
                  {s.num ?? <span className="inline-block h-6 w-12 animate-pulse rounded bg-muted align-middle" aria-label="Loading" />}
                </div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
              {i < statItems.length - 1 && (
                <span className="ml-3 hidden h-9 w-px bg-border lg:inline-block" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ============================================================ */}
      {/*  BANNERS - one static, one clickable promo                  */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-[1440px] px-4 py-9 sm:px-8 lg:px-12">
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Static banner image — not clickable. Aspect ratio is configurable
              from mongo (banners.static.aspect_ratio) so it matches your image. */}
          <div
            className="overflow-hidden rounded-2xl border border-border bg-secondary"
            style={{ aspectRatio: bannerRatio(staticBanner) }}
          >
            {staticBanner.image_url ? (
              <img
                src={staticBanner.image_url}
                alt={staticBanner.title || 'Recovry announcement'}
                className="h-full w-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <span className="flex items-center gap-2 text-sm">
                  <ImageIcon className="size-5" /> Banner image
                </span>
              </div>
            )}
          </div>

          {/* Promo banner image — clickable, redirects to the mongo link. */}
          <a
            href={promoBanner.link || '#'}
            target={promoBanner.link?.startsWith('http') ? '_blank' : undefined}
            rel={promoBanner.link?.startsWith('http') ? 'noopener noreferrer' : undefined}
            onClick={(e) => {
              if (!promoBanner.link) {
                e.preventDefault();
              } else if (promoBanner.link.startsWith('/')) {
                e.preventDefault();
                navigate(promoBanner.link);
              }
            }}
            aria-label={promoBanner.title || 'Promotion'}
            className="group block overflow-hidden rounded-2xl border border-border bg-secondary"
            style={{ aspectRatio: bannerRatio(promoBanner) }}
          >
            {promoBanner.image_url ? (
              <img
                src={promoBanner.image_url}
                alt={promoBanner.title || 'Recovry promotion'}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <span className="flex items-center gap-2 text-sm">
                  <ImageIcon className="size-5" /> Promo banner image
                </span>
              </div>
            )}
          </a>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  HOW IT WORKS                                                */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-[1440px] px-4 py-14 sm:px-8 lg:px-12">
        <FadeIn>
          <div className="text-xs font-bold uppercase tracking-wider text-primary">Simple process</div>
          <h2 className="mt-2.5 text-[32px] font-extrabold tracking-tight text-foreground">
            From symptom to session in 3 steps
          </h2>
          <p className="mt-2.5 max-w-md text-[15px] leading-relaxed text-muted-foreground">
            No calls, no queues, no confusion. Find a clinic, pick your service, and book instantly.
          </p>
        </FadeIn>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {steps.map((step, i) => (
            <FadeIn key={step.title} delay={i * 0.1}>
              <div className="relative h-full rounded-2xl border border-border bg-card p-6 pt-7">
                <span className="absolute -top-3 left-6 flex size-7 items-center justify-center rounded-full bg-primary text-xs font-extrabold text-primary-foreground">
                  {i + 1}
                </span>
                <span className="mb-4 flex size-11 items-center justify-center rounded-xl bg-secondary text-primary">
                  <step.icon className="size-5" />
                </span>
                <h3 className="text-[17px] font-bold text-foreground">{step.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">{step.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/*  TESTIMONIALS (from mongo - hidden when there are none)      */}
      {/* ============================================================ */}
      {testimonials.length > 0 && (
      <section className="mx-auto max-w-[1440px] px-4 pb-14 sm:px-8 lg:px-12">
        <FadeIn>
          <div className="text-xs font-bold uppercase tracking-wider text-primary">Patient stories</div>
          <h2 className="mt-2.5 text-[32px] font-extrabold tracking-tight text-foreground">What patients say</h2>
        </FadeIn>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {testimonials.map((t, i) => (
            <FadeIn key={`${t.name}-${i}`} delay={i * 0.1}>
              <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-5">
                <div
                  className="mb-2 tracking-[2px] text-[var(--sc-amber)]"
                  aria-label={`${t.rating} out of 5 stars`}
                >
                  {'★'.repeat(t.rating)}
                  <span className="text-muted-foreground/30">{'★'.repeat(5 - t.rating)}</span>
                </div>
                <ReviewText text={t.text} lines={3} reserveToggle className="text-[13.5px] leading-relaxed text-muted-foreground" />
                <div className="mt-auto flex items-center gap-2.5 pt-4">
                  <span
                    className="flex size-9 items-center justify-center rounded-full text-[13px] font-bold text-white"
                    style={{ background: t.color }}
                  >
                    {t.avatar}
                  </span>
                  <div>
                    <div className="text-[13px] font-semibold text-foreground">{t.name}</div>
                    <div className="text-[11px] text-[var(--sc-hint)]">{t.detail}</div>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>
      )}

      {/* ============================================================ */}
      {/*  CLINIC CTA                                                  */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-[1440px] px-4 pb-16 sm:px-8 lg:px-12">
        <FadeIn>
          <div
            className="flex flex-col items-start justify-between gap-8 rounded-3xl p-6 sm:p-12 lg:flex-row lg:items-center"
            style={{ background: 'linear-gradient(120deg, var(--sc-teal-dark), var(--sc-teal) 60%, var(--sc-teal-mid))' }}
          >
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-white/75">For physiotherapists</div>
              <h2 className="mt-2.5 text-[30px] font-extrabold tracking-tight text-white">
                Are you a physiotherapy practitioner?
              </h2>
              <p className="mt-2.5 max-w-md text-[14.5px] leading-relaxed text-white/85">
                Join India's growing physiotherapy network. Get discovered by patients searching for
                exactly your services, at zero setup cost during our launch phase.
              </p>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {clinicBenefits.map((b) => (
                  <span
                    key={b.label}
                    className="flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-[12.5px] text-white"
                  >
                    <b.icon className="size-4" /> {b.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex w-full shrink-0 flex-col gap-3 lg:w-auto">
              <button
                onClick={() => navigate('/partners')}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-white px-7 py-3 text-[14.5px] font-bold text-[var(--sc-teal-dark)] transition-transform hover:scale-[1.02]"
              >
                Partner with Recovry <ArrowRight className="size-4" />
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="rounded-lg border-2 border-white/45 px-7 py-3 text-[14.5px] font-semibold text-white transition-colors hover:bg-white/10"
              >
                Talk to us first
              </button>
            </div>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
